import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { CheckCircle, LucideIcon } from 'lucide-react';
import { logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { captureAttribution } from '@/utils/emailAttribution';
import ErrorBoundary from '@/shared/components/ErrorBoundary';

export interface LandingPageConfig {
  examType: 'lbsw' | 'lmsw' | 'lcsw';
  /** Clean route path for this landing (e.g. '/lcsw'); drives canonical + og:url. */
  path: string;
  meta: {
    title: string;
    description: string;
    /** Absolute OG/Twitter card image URL. Falls back to the default OG image. */
    ogImage?: string;
  };
  hero: {
    icon: LucideIcon;
    iconBgColor: string;
    titlePrefix: string;
    titleGradient: string;
    subtitle: string;
  };
  colors: {
    gradient: string; // Background gradient
    iconBg: string;   // Hero icon background
    ctaGradient: string; // CTA button gradient
  };
  features: {
    title: string;
    items: Array<{
      title: string;
      description: string;
    }>;
  };
  cta: {
    heading: string;
    ctaText: string;
  };
  /**
   * One line under the hero pointing at the public state licensing pages, for
   * the visitor who does not yet know which exam their board asks for first.
   * Optional: a landing with no licensing question omits it.
   */
  licensingNote?: string;
}

interface LandingPageTemplateProps {
  config: LandingPageConfig;
}

const SITE_URL = 'https://www.socialworktestprepacademy.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/default-og-image.png`;

const LandingPageTemplate: React.FC<LandingPageTemplateProps> = ({ config }) => {
  useEffect(() => {
    const attribution = captureAttribution();

    logAnalyticsEvent(ANALYTICS_EVENTS.LANDING_PAGE_VIEWED, {
      page_name: config.examType,
      exam_type: config.examType,
      acquisition_channel: attribution.acquisitionChannel,
      utm_source: attribution.utmParams.utm_source,
      utm_campaign: attribution.utmParams.utm_campaign,
      referrer: attribution.referrer,
      timestamp: new Date().toISOString(),
    });
  }, [config.examType]);

  const Icon = config.hero.icon;

  // Clean canonical built from the config path — NOT window.location.href, which
  // would fold UTM/ref query strings into the canonical and self-compete every
  // campaign link (the pollution bug flagged in the SEO audit).
  const canonicalUrl = `${SITE_URL}${config.path}`;
  const ogImage = config.meta.ogImage ?? DEFAULT_OG_IMAGE;

  return (
    <ErrorBoundary>
      <Helmet>
        <title>{config.meta.title}</title>
        <meta name="description" content={config.meta.description} />

        {/* Open Graph */}
        <meta property="og:title" content={config.meta.title} />
        <meta property="og:description" content={config.meta.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="Social Work Test Prep Academy" />

        {/* Twitter card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config.meta.title} />
        <meta name="twitter:description" content={config.meta.description} />
        <meta name="twitter:image" content={ogImage} />

        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <div className={`min-h-screen ${config.colors.gradient}`}>
        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className={`inline-flex items-center justify-center w-16 h-16 ${config.colors.iconBg} rounded-2xl shadow-ledge mb-4`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h1 className="heading-1 md:text-5xl mb-4">
              {config.hero.titlePrefix} <span className={config.hero.titleGradient}>{config.examType.toUpperCase()} Exam</span>
            </h1>
            <p className="body-large text-xl mb-2">
              {config.hero.subtitle}
            </p>
            <p className="body-base text-neutral-500">
              Get 7 free days • No card, no billing, nothing to cancel
            </p>
            {config.licensingNote && (
              <p className="body-base text-neutral-500 mt-2">
                {config.licensingNote}{' '}
                <Link to="/licensing" className="font-semibold text-primary-600 underline">
                  See your state.
                </Link>
              </p>
            )}
          </div>

          {/* Value Proposition */}
          <div className="card mb-8 p-8">
            <h2 className="heading-2 text-2xl mb-6 text-center">
              {config.features.title}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {config.features.items.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display font-semibold text-neutral-900">{feature.title}</h3>
                    <p className="body-small">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className={`${config.colors.ctaGradient} rounded-2xl shadow-ledge-lg p-8 text-white text-center`}>
            <h2 className="font-display font-bold text-3xl mb-4">{config.cta.heading}</h2>
            <p className="text-lg mb-6 opacity-90">
              Join social workers around the nation who are preparing to pass the ASWB exams
            </p>
            <Link
              to="/try"
              className="inline-block bg-white text-neutral-900 px-8 py-4 rounded-xl font-display font-semibold text-lg hover:bg-neutral-50 transition-colors shadow-ledge"
            >
              {config.cta.ctaText}
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
            <div className="p-6">
              <div className="font-display text-3xl font-bold text-neutral-900 mb-2">7-Day</div>
              <div className="text-neutral-600">Free Access</div>
            </div>
            <div className="p-6">
              <div className="font-display text-3xl font-bold text-neutral-900 mb-2">500+</div>
              <div className="text-neutral-600">Practice Questions</div>
            </div>
            <div className="p-6">
              <div className="font-display text-3xl font-bold text-neutral-900 mb-2">7-Day</div>
              <div className="text-neutral-600">Money-Back Guarantee</div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="mt-12 text-center">
            <p className="text-neutral-600 mb-4">
              Ready to ace your {config.examType.toUpperCase()} exam?
            </p>
            <Link
              to="/try"
              className={`inline-block ${config.colors.iconBg} text-white px-8 py-3 rounded-xl font-display font-semibold shadow-ledge hover:opacity-90 transition-opacity`}
            >
              Get 7 Free Days →
            </Link>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default LandingPageTemplate;
